import React, { ChangeEvent, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios, { CancelToken } from "axios";
import ReactDOM from "react-dom";
import moment from "moment";

import { StatusCode } from "api/protocols";

import {
  GetLeague,
  PutLeague,
  UploadLeagueLogo,
  DeleteLeagueLogo,
  ILeaguePut,
  DeleteLeague,
  GetLeagueSponsorship,
  PostLeagueSponsorship,
  DeleteLeagueSponsorship,
} from "api/rpc/2024-04/facilityAdmin/league/league";

import { showError, showSuccess } from "redux/actions/ui";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { formatDate, handleChangeEventInput } from "helpers/Helpers";

import { TWeekDays } from "components/form/calendarField/helpers";
import Page from "components/page/Page";
import Card from "components/card/Card";
import Form from "components/form/Form";
import FormLayout from "components/form/FormLayout";
import Input from "components/form/input/Input";
import Popup from "components/popup/Popup";
import TextEditor from "components/textEditor/textEditor";
import Checkbox from "components/form/checkbox/Checkbox";
import TimePick from "components/timePick/TimePick";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import Portal from "elements/Portal";
import CustomerImage from "elements/customer/CustomerImage";
import SponsorList, { ISponsorshipImage } from "components/sponsorList/SponsorList";
import useModal from "hooks/modals/useModal";

interface ILeagueState {
  id: number | string;
  name?: string;
  week_day?: TWeekDays;
  start_time?: string;
  description?: string;
  register_link: string;
  leaderboard_link: string;
  logo_source: string;
  display_online: boolean;
  display_registration_online: boolean;
  start_date: string;
  end_date: string;
}

export default function SingleLeagueSettings() {
  const history = useHistory();
  const { leagueId } = useParams<{ leagueId: string }>();
  const { t } = useTranslation();

  const { facilityStore } = useAppSelector(store => store);
  const dispatch = useAppDispatch();

  const [league, setLeague] = useState<ILeagueState>({
    id: leagueId,
    name: "",
    week_day: null,
    start_time: "12:00",
    description: "",
    register_link: "",
    leaderboard_link: "",
    logo_source: null,
    display_online: false,
    display_registration_online: false,
    start_date: "",
    end_date: "",
  });

  const [leagueTimes, setLeagueTimes] = useState({
    start_time: "12:00",
    start_date: new Date(),
    end_date: new Date(),
  });

  const [sponsorships, setSponsorships] = useState<ISponsorshipImage[] | undefined>(undefined);
  const [deleteLeaguePopup, setDeleteLeaguePopup] = useState(false);

  const { state: removeSponsorshipPopup, updateModal, closeModal } = useModal({
    sponsorId: null as number | null,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();
    if (facilityStore.facility) {
      void loadLeague(leagueId, source.token);
      void loadSponsorships(leagueId, source.token);
    }

    return () => {
      source.cancel();
    };
  }, [leagueId, facilityStore]);

  async function loadLeague(id?: string, token?: CancelToken) {
    const leagueRes = await GetLeague({ id: id }, true, token);

    if (token && token.reason) {
      return;
    }
    if (leagueRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof leagueRes.data === "string" ? leagueRes.data : t("secure.facility.league.league_settings.002"),
        ),
      );
      history.push(`/admin/league/${leagueId}/home`);
      return;
    }

    ReactDOM.unstable_batchedUpdates(() => {
      setLeague(prev => ({
        ...prev,
        name: leagueRes.data[0]?.name,
        start_time: leagueRes.data[0]?.start_time,
        week_day: leagueRes.data[0]?.week_day,
        description: leagueRes.data[0]?.description,
        logo_source: leagueRes.data[0]?.logo_source,
        display_online: leagueRes.data[0]?.display_online,
        display_registration_online: leagueRes.data[0]?.display_registration_online,
      }));
      setLeagueTimes(prevState => ({
        ...prevState,
        start_date: leagueRes.data[0]?.start_date
          ? new Date(moment(leagueRes.data[0]?.start_date).format())
          : prevState.start_date,
        end_date: leagueRes.data[0]?.end_date
          ? new Date(moment(leagueRes.data[0]?.end_date).format())
          : prevState.end_date,
      }));
    });
  }

  async function loadSponsorships(id: string, token?: CancelToken) {
    const res = await GetLeagueSponsorship({ league_id: id }, false, token);

    if (token && token.reason) {
      return;
    }
    if (res.status !== StatusCode.OK) {
      setSponsorships([]);
      return;
    }

    const loadedSponsorships: ISponsorshipImage[] = Array.isArray(res.data) ? res.data : [];
    setSponsorships(loadedSponsorships);
  }

  async function uploadSponsorship(image: File) {
    const formData = new FormData();
    formData.append("image", image);
    formData.append("league_id", leagueId);

    const res = await PostLeagueSponsorship(formData, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error uploading sponsor")));
      return;
    }

    void loadSponsorships(leagueId);
  }

  async function removeSponsorship() {
    if (removeSponsorshipPopup.sponsorId == null) {
      return;
    }

    const res = await DeleteLeagueSponsorship({ id: removeSponsorshipPopup.sponsorId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("Error removing sponsor")));
      return;
    }

    setSponsorships(prev => (prev ?? []).filter(sponsor => sponsor.id !== removeSponsorshipPopup.sponsorId));
    closeModal();
  }

  async function updateLeague(params: ILeaguePut) {
    const leagueParams = {
      ...params,
      start_date: formatDate(leagueTimes.start_date),
      end_date: formatDate(leagueTimes.end_date),
    };

    const updateRes = await PutLeague(leagueParams, true);

    if (updateRes.status !== StatusCode.OK) {
      dispatch(
        showError(
          typeof updateRes.data === "string" ? updateRes.data : t("secure.facility.league.league_settings.003"),
        ),
      );
      return;
    }
    dispatch(showSuccess(t("secure.facility.league.league_settings.004")));
  }

  async function removeLeague() {
    const res = await DeleteLeague({ id: leagueId }, true);

    if (res.status !== StatusCode.OK) {
      dispatch(showError(typeof res.data === "string" ? res.data : t("There was an error deleting the league.")));
      setDeleteLeaguePopup(false);
      return;
    }

    setDeleteLeaguePopup(false);
    history.push("/admin/events");
  }

  async function saveLeagueLogo(imageFile: File) {
    const formData = new FormData();

    formData.append("image", imageFile);
    formData.append("league_id", league.id.toString());

    const imageRes = await UploadLeagueLogo(formData, true);
    if (imageRes.status !== StatusCode.OK) {
      return;
    }

    setLeague(prevState => ({ ...prevState, logo_source: imageRes.data.logo_source }));
  }

  async function deleteLeagueLogo(league_id: string) {
    const deleteRes = await DeleteLeagueLogo({ league_id }, true);

    if (deleteRes.status !== StatusCode.OK) {
      return;
    }

    setLeague(prevState => ({ ...prevState, logo_source: deleteRes.data.logo_source }));
  }

  function setDate(date: Date, key: string) {
    setLeagueTimes(prevState => ({ ...prevState, [key]: date }));
  }

  const primaryAction = {
    content: t("secure.facility.league.league_settings.005"),
    action: () => updateLeague(league),
  };

  const secondaryAction = [
    {
      content: t("Delete"),
      action: () => setDeleteLeaguePopup(true),
    },
  ];

  return (
    <Page
      title={t("secure.facility.league.league_settings.006")}
      narrow
      primaryAction={primaryAction}
      secondaryActions={secondaryAction}
    >
      <Form>
        <Card>
          <Card.Section>
            <Input
              value={league.name}
              label={t("secure.facility.league.new.012")}
              id="name"
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleChangeEventInput(e, league, setLeague)}
              placeholder={t("secure.facility.league.new.012")}
            />
          </Card.Section>
        </Card>

        <Card title={t("secure.facility.league.league_settings.007")}>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <Checkbox
                  id="display_online"
                  size="medium"
                  value={league.display_online}
                  checked={league.display_online}
                  onChange={() => setLeague(prevState => ({ ...prevState, display_online: !league.display_online }))}
                  label={t("Display Online")}
                  supportText={t("Would you like to publicly display this league in the client portal list and calendar?")}
                />
                <Checkbox
                  id="display_registration_online"
                  size="medium"
                  value={league.display_registration_online}
                  checked={league.display_registration_online}
                  onChange={() =>
                    setLeague(prevState => ({
                      ...prevState,
                      display_registration_online: !league.display_registration_online,
                    }))
                  }
                  label={t("Display Registration Online")}
                  supportText={t("Would you like to show online registration for this league?")}
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
          <Card.Section>
            <FormLayout>
              <FormLayout.Group>
                <TimePick
                  label={t("Start Time")}
                  value={league.start_time}
                  onChange={timeString => setLeague(prev => ({ ...prev, start_time: timeString }))}
                  size="large"
                  align="right"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <DatePickerInput
                  label={t("Start Date")}
                  startingDate={leagueTimes.start_date}
                  setStartingDate={(date: Date) => setDate(date, "start_date")}
                  months={1}
                  position="left"
                />

                <DatePickerInput
                  label={t("End Date")}
                  startingDate={leagueTimes.end_date}
                  setStartingDate={(date: Date) => setDate(date, "end_date")}
                  months={1}
                  position="left"
                />
              </FormLayout.Group>
            </FormLayout>
          </Card.Section>
          <Card.Section title={t("Logo")}>
            <div className="stats_bar-picture">
              <CustomerImage
                imageSource={league.logo_source}
                saveProfileImage={imageFile => saveLeagueLogo(imageFile)}
                deleteProfileImage={() => deleteLeagueLogo(leagueId)}
              />
            </div>
          </Card.Section>
          <Card.Section>
            <TextEditor
              markdownText={league.description ?? ""}
              markdownTextOnChange={description => setLeague(prev => ({ ...prev, description: description }))}
              label={t("Description")}
            />
          </Card.Section>
        </Card>
      </Form>

      <div className="mt-4">
        <Card title={t("Sponsors")}>
          <Card.Section>
            <SponsorList
              sponsors={sponsorships}
              uploadLabel={t("Upload sponsor")}
              onNew={image => void uploadSponsorship(image)}
              onRemove={sponsorId => updateModal({ isOpen: true, sponsorId })}
            />
          </Card.Section>
        </Card>
      </div>

      <Portal isMounted={removeSponsorshipPopup.isOpen}>
        <Popup
          open
          type="warning"
          title={t("Remove sponsor")}
          description={t("Are you sure you want to remove this sponsor?")}
          onCancel={() => closeModal()}
          onOk={() => void removeSponsorship()}
          okText={t("Remove")}
        />
      </Portal>

      <Portal isMounted={deleteLeaguePopup}>
        <Popup
          open={deleteLeaguePopup}
          type={"warning"}
          title={t("Warning!")}
          description={t("Are you sure you want to delete this League?")}
          onCancel={() => setDeleteLeaguePopup(false)}
          onOk={() => removeLeague()}
          okText={t("Remove")}
        />
      </Portal>
    </Page>
  );
}
